import {
  DebtCustomer,
  DebtRecommendation,
  WorkflowState,
  useDebtHandOverManyMutation,
  useDebtPayoutsCsvLazyQuery,
  useDebtSyncNowMutation,
  useDebtOverviewQuery,
} from '@dieterApi/debtCollection';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import MUIDataTable, { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { DebtCaseFileDialog } from './DebtCaseFileDialog';
import {
  RECOMMENDATION_LABELS,
  RECOMMENDATION_ORDER,
  SIGNAL_CHIP,
  SUBSCRIPTION_LABELS,
  DEBTIST_STATUS_LABELS,
  WORKFLOW_LABELS,
  formatDate,
  formatEur,
  handoverTargetLabel,
} from './debtCollectionLabels';

// Inkasso-Cockpit: offene Posten live aus Stripe, eine Zeile pro Kunde,
// Empfehlung aus einfachen Signalen, Übergabe an Debtist per Klick (einzeln in
// der Akte oder gesammelt über die Auswahl). Konzept-Doc „Inkasso-Übergabe an Debtist“.

type ViewFilter =
  | 'decide'
  | 'followUp'
  | 'feedback'
  | 'parked'
  | 'debtist'
  | 'writtenOff'
  | 'closed'
  | 'dunning'
  | 'all';
type RecommendationFilter = 'all' | DebtRecommendation;

const inStates =
  (...states: WorkflowState[]) =>
  (c: DebtCustomer) =>
    states.includes(c.workflowState);

const VIEWS: Record<ViewFilter, { label: string; match: (c: DebtCustomer) => boolean }> = {
  decide: { label: 'Zu entscheiden (Mahnung durch)', match: inStates('DECIDE') },
  followUp: { label: 'Nachforderungen', match: inStates('FOLLOW_UP') },
  feedback: { label: 'Rückfragen von Debtist', match: (c) => c.feedbackRequired },
  parked: { label: 'Zurückgestellt / selbst klären', match: inStates('PARKED') },
  debtist: { label: 'Bei Debtist', match: inStates('AT_DEBTIST', 'IN_PROGRESS') },
  writtenOff: { label: 'Ausgebucht', match: inStates('WRITTEN_OFF') },
  closed: { label: 'Abgeschlossen (eingetrieben, geschlossen, zurückgezogen)', match: inStates('CLOSED') },
  dunning: { label: 'Mahnung läuft noch', match: inStates('DUNNING') },
  all: { label: 'Alle offenen Posten', match: () => true },
};

// In diesen Ansichten kann die Auswahl gesammelt übergeben werden
const SELECTABLE_VIEWS: ViewFilter[] = ['decide', 'followUp'];

export function AdminDebtCollection() {
  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState<ViewFilter>('decide');
  const [recommendationFilter, setRecommendationFilter] = useState<RecommendationFilter>('all');
  const [openCustomerId, setOpenCustomerId] = useState<string | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [confirmMany, setConfirmMany] = useState(false);
  const { data, loading, error, refetch } = useDebtOverviewQuery();
  const [handOverMany, { loading: handingOver }] = useDebtHandOverManyMutation();
  const [syncNow, { loading: syncing }] = useDebtSyncNowMutation();
  const [loadPayouts, { loading: loadingPayouts }] = useDebtPayoutsCsvLazyQuery();
  const [payoutsOpen, setPayoutsOpen] = useState(false);
  const [payoutMonth, setPayoutMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });

  const runSync = async () => {
    try {
      const r = (await syncNow()).data?.debtSyncNow;
      enqueueSnackbar(
        `Abgleich: ${r?.changed ?? 0} von ${r?.checked ?? 0} Fällen geändert, ${r?.collected ?? 0} eingetrieben, ${
          r?.feedbackRequired ?? 0
        } neue Rückfragen${r?.errors.length ? `, ${r.errors.length} Fehler` : ''}`,
        { variant: r?.errors.length ? 'warning' : 'success' }
      );
      refetch({ refresh: false });
    } catch (e: any) {
      enqueueSnackbar(`Abgleich fehlgeschlagen: ${e.message}`, { variant: 'error' });
    }
  };

  // Debtist-Auszahlungen eines Monats als CSV herunterladen (für DATEV/Buchhaltung)
  const downloadPayouts = async () => {
    const [year, month] = payoutMonth.split('-').map(Number);
    const from = `${payoutMonth}-01`;
    const to = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
    const result = await loadPayouts({ variables: { from, to } });
    if (result.error || result.data?.debtPayoutsCsv === undefined) {
      enqueueSnackbar(`Export fehlgeschlagen: ${result.error?.message ?? 'keine Daten'}`, { variant: 'error' });
      return;
    }
    const blob = new Blob([result.data.debtPayoutsCsv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `debtist-auszahlungen-${payoutMonth}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setPayoutsOpen(false);
  };

  const overview = data?.debtOverview;
  const customers = useMemo(() => {
    return (overview?.customers ?? []).filter(
      (c) => VIEWS[view].match(c) && (recommendationFilter === 'all' || c.recommendation === recommendationFilter)
    );
  }, [overview, view, recommendationFilter]);
  const total = useMemo(() => customers.reduce((sum, c) => sum + c.openAmount, 0), [customers]);
  const selectedCustomers = useMemo(() => selected.map((i) => customers[i]).filter(Boolean), [selected, customers]);

  // Laufende Übergaben (Sammelübergabe im Hintergrund): alle 5 s den Fallstand
  // nachladen. Eigenes Intervall statt Apollo-Polling, weil das die Variablen
  // des letzten refetch übernimmt (refresh: true = Stripe komplett neu)
  const [watching, setWatching] = useState(false);
  const inProgress = (overview?.customers ?? []).some((c) => c.workflowState === 'IN_PROGRESS');
  useEffect(() => {
    if (!inProgress && !watching) return;
    const timer = setInterval(() => refetch({ refresh: false }), 5000);
    // nach dem ersten Nachladen übernimmt inProgress
    const stopWatching = setTimeout(() => setWatching(false), 15000);
    return () => {
      clearInterval(timer);
      clearTimeout(stopWatching);
    };
  }, [inProgress, watching, refetch]);

  useEffect(() => setSelected([]), [view, recommendationFilter]);

  const startHandOverMany = async () => {
    try {
      const ids = selectedCustomers.map((c) => c.stripeCustomerId);
      await handOverMany({ variables: { stripeCustomerIds: ids } });
      enqueueSnackbar(`Übergabe von ${ids.length} Kunden gestartet, der Stand aktualisiert sich automatisch`, {
        variant: 'success',
      });
      setConfirmMany(false);
      setSelected([]);
      setWatching(true);
    } catch (e: any) {
      enqueueSnackbar(`Übergabe nicht gestartet: ${e.message}`, { variant: 'error' });
    }
  };

  const columns: MUIDataTableColumn[] = useMemo(
    () => [
      {
        name: 'name',
        label: 'Kunde',
        options: {
          customBodyRenderLite: (index) => {
            const c = customers[index];
            return (
              <div>
                <div>{c.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#888' }}>
                  {c.companyName && c.companyName !== c.name ? `${c.companyName} · ` : ''}
                  {c.email || c.stripeCustomerId}
                </div>
              </div>
            );
          },
        },
      },
      {
        name: 'openAmount',
        label: 'Offen',
        options: { customBodyRenderLite: (index) => formatEur(customers[index].openAmount) },
      },
      { name: 'invoiceCount', label: 'Rechnungen' },
      {
        name: 'oldestDueDate',
        label: 'Älteste Fälligkeit',
        options: { customBodyRenderLite: (index) => formatDate(customers[index].oldestDueDate) },
      },
      {
        name: 'subscription',
        label: 'Abo',
        options: {
          customBodyRenderLite: (index) =>
            customers[index].subscriptionStatuses.map((s) => SUBSCRIPTION_LABELS[s] ?? s).join(', ') || 'beendet',
        },
      },
      {
        name: 'paidTotal',
        label: 'Bisher bezahlt',
        options: {
          customBodyRenderLite: (index) => {
            const c = customers[index];
            return c.paidInvoiceCount ? `${formatEur(c.paidTotal)} (${c.paidInvoiceCount})` : '—';
          },
        },
      },
      {
        name: 'lastLogin',
        label: 'Letzter Login',
        options: { customBodyRenderLite: (index) => formatDate(customers[index].lastLogin) },
      },
      {
        name: 'signals',
        label: 'Signale',
        options: {
          sort: false,
          customBodyRenderLite: (index) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 360 }}>
              {customers[index].signals
                .filter((s) => s.level !== 'info')
                .map((s) => (
                  <Chip key={s.key} label={s.label} size="small" {...SIGNAL_CHIP[s.level]} />
                ))}
            </div>
          ),
        },
      },
      {
        name: 'recommendation',
        label: 'Empfehlung',
        options: {
          customBodyRenderLite: (index) => {
            const r = RECOMMENDATION_LABELS[customers[index].recommendation];
            return <Chip label={r.label} color={r.color} size="small" />;
          },
        },
      },
      {
        name: 'workflowState',
        label: 'Fall',
        options: {
          customBodyRenderLite: (index) => {
            const c = customers[index];
            const w = WORKFLOW_LABELS[c.workflowState];
            return (
              <div>
                <Chip label={w.label} color={w.color} size="small" variant="outlined" />
                {c.debtistReference && (
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>
                    Az. {c.debtistReference}
                    {c.debtistStatus ? ` · ${DEBTIST_STATUS_LABELS[c.debtistStatus] ?? c.debtistStatus}` : ''}
                    {c.debtistTotalPaid ? ` · ${formatEur(c.debtistTotalPaid)} gezahlt` : ''}
                  </div>
                )}
                {c.workflowState === 'PARKED' && c.holdUntil && (
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>bis {formatDate(c.holdUntil)}</div>
                )}
                {c.feedbackRequired && (
                  <Chip label="Rückfrage von Debtist" color="warning" size="small" sx={{ mt: 0.5 }} />
                )}
                {c.caseError && <div style={{ fontSize: '0.75rem', color: '#c62828' }}>{c.caseError}</div>}
              </div>
            );
          },
        },
      },
    ],
    [customers]
  );

  // Sortierwerte; die Darstellung übernimmt customBodyRenderLite
  const tableData = useMemo(
    () =>
      customers.map((c) => ({
        name: c.name,
        openAmount: c.openAmount,
        invoiceCount: c.invoiceCount,
        oldestDueDate: c.oldestDueDate ?? '',
        subscription: c.subscriptionStatuses.join(','),
        paidTotal: c.paidTotal,
        lastLogin: c.lastLogin ?? '',
        signals: '',
        recommendation: RECOMMENDATION_ORDER.indexOf(c.recommendation),
        workflowState: c.workflowState,
      })),
    [customers]
  );

  const selectable = SELECTABLE_VIEWS.includes(view) && Boolean(overview?.handoverEnabled);

  const options: MUIDataTableOptions = useMemo(
    () => ({
      responsive: 'standard',
      selectableRows: selectable ? 'multiple' : 'none',
      rowsSelected: selected,
      onRowSelectionChange: (_current, _all, rowsSelected) => setSelected(rowsSelected ?? []),
      // fehlende Schuldnerdaten lassen sich nur einzeln in der Akte ergänzen
      isRowSelectable: (dataIndex) => customers[dataIndex]?.debtor.missing.length === 0,
      selectToolbarPlacement: 'none',
      download: false,
      print: false,
      filter: false,
      viewColumns: false,
      rowsPerPage: 50,
      rowsPerPageOptions: [25, 50, 100, 300],
      sortOrder: { name: 'openAmount', direction: 'desc' },
      onRowClick: (_row, meta) => setOpenCustomerId(customers[meta.dataIndex]?.stripeCustomerId ?? null),
      setRowProps: () => ({ style: { cursor: 'pointer' } }),
      textLabels: {
        body: {
          noMatch: loading
            ? 'Lade Live-Daten aus Stripe (beim ersten Aufruf bis ca. 30 Sekunden) …'
            : 'Keine offenen Posten in dieser Ansicht',
        },
        pagination: { rowsPerPage: 'Zeilen pro Seite:', displayRows: 'von' },
        toolbar: { search: 'Suche' },
      },
    }),
    [loading, customers, selectable, selected]
  );

  const selectedTotal = selectedCustomers.reduce((sum, c) => sum + c.openAmount, 0);
  const selectedNotRecommended = selectedCustomers.filter((c) => c.recommendation !== 'HANDOVER').length;

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', margin: '16px 0', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="debt-view-filter">Ansicht</InputLabel>
          <Select
            labelId="debt-view-filter"
            label="Ansicht"
            value={view}
            onChange={(event) => setView(event.target.value as ViewFilter)}
          >
            {(Object.keys(VIEWS) as ViewFilter[]).map((key) => {
              const count = (overview?.customers ?? []).filter(VIEWS[key].match).length;
              return (
                <MenuItem key={key} value={key}>
                  {VIEWS[key].label}
                  {overview ? ` (${count})` : ''}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="debt-recommendation-filter">Empfehlung</InputLabel>
          <Select
            labelId="debt-recommendation-filter"
            label="Empfehlung"
            value={recommendationFilter}
            onChange={(event) => setRecommendationFilter(event.target.value as RecommendationFilter)}
          >
            <MenuItem value="all">Alle</MenuItem>
            {RECOMMENDATION_ORDER.map((r) => (
              <MenuItem key={r} value={r}>
                {RECOMMENDATION_LABELS[r].label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectable && (
          <Button
            variant="contained"
            disabled={selectedCustomers.length === 0 || handingOver}
            onClick={() => setConfirmMany(true)}
          >
            {selectedCustomers.length
              ? `${selectedCustomers.length} an Debtist übergeben`
              : 'Auswahl an Debtist übergeben'}
          </Button>
        )}
        {loading && <CircularProgress size={20} />}
        <div style={{ marginLeft: 'auto', color: '#888', fontSize: '0.875rem', display: 'flex', alignItems: 'center' }}>
          {customers.length} Kunden · {formatEur(total)} offen
          {overview && (
            <>
              {' · Stand '}
              {new Date(overview.generatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
              {' · '}
              {overview.handoverEnabled ? handoverTargetLabel(overview.handoverTarget) : 'Übergabe aus'}
            </>
          )}
          {overview?.handoverEnabled && (
            <>
              <Tooltip title="Stand aller Fälle jetzt bei Debtist abfragen (läuft sonst täglich um 6 Uhr)">
                <span>
                  <Button size="small" disabled={syncing} onClick={runSync}>
                    {syncing ? <CircularProgress size={14} /> : 'Abgleich jetzt'}
                  </Button>
                </span>
              </Tooltip>
              <Button size="small" onClick={() => setPayoutsOpen(true)}>
                Auszahlungen (CSV)
              </Button>
            </>
          )}
          <Tooltip title="Neu aus Stripe laden">
            <span>
              <IconButton size="small" disabled={loading} onClick={() => refetch({ refresh: true })}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
      {overview && !overview.handoverEnabled && (
        <div style={{ color: '#888', fontSize: '0.875rem', marginBottom: 12 }}>
          Übergabe an Debtist nicht möglich: {overview.handoverBlockedReason}
        </div>
      )}
      {error && <div style={{ color: '#c62828', marginBottom: 16 }}>Fehler beim Laden: {error.message}</div>}
      <MUIDataTable title="" data={tableData} columns={columns} options={options} />
      <DebtCaseFileDialog
        stripeCustomerId={openCustomerId}
        handoverEnabled={Boolean(overview?.handoverEnabled)}
        handoverTarget={overview?.handoverTarget ?? null}
        onClose={() => setOpenCustomerId(null)}
        onChanged={() => refetch({ refresh: false })}
      />
      <Dialog open={payoutsOpen} onClose={() => setPayoutsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Debtist-Auszahlungen exportieren</DialogTitle>
        <DialogContent>
          <TextField
            type="month"
            size="small"
            label="Monat"
            value={payoutMonth}
            onChange={(event) => setPayoutMonth(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1 }}
          />
          <p style={{ color: '#888', fontSize: '0.875rem', marginTop: 12 }}>
            Eine Zeile je ausgezahlter Forderung: Zahlungseingang, Inkassogebühren, Auszahlung, Debtist-Abrechnung
            (Semikolon, Dezimalkomma).
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutsOpen(false)}>Abbrechen</Button>
          <Button variant="contained" disabled={loadingPayouts} onClick={downloadPayouts}>
            Herunterladen
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmMany} onClose={() => setConfirmMany(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedCustomers.length} Kunden an Debtist übergeben?</DialogTitle>
        <DialogContent>
          <p>
            Alle offenen Rechnungen dieser Kunden über zusammen <strong>{formatEur(selectedTotal)}</strong> gehen an{' '}
            <strong>{handoverTargetLabel(overview?.handoverTarget)}</strong>, Stufe Inkasso (Precourt). Die Abos laufen
            weiter.
          </p>
          {selectedNotRecommended > 0 && (
            <p style={{ color: '#c62828', marginTop: 12 }}>
              Bei {selectedNotRecommended} davon lautet die Empfehlung nicht „Übergeben“.
            </p>
          )}
          <p style={{ color: '#888', marginTop: 12, fontSize: '0.875rem' }}>
            Die Übergabe läuft im Hintergrund, Kunde für Kunde. Der Stand aktualisiert sich hier automatisch.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMany(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={startHandOverMany} disabled={handingOver}>
            Übergeben
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
